<template>
    <vx-dropdown ref="dropdown" vs-custom-content :vs-trigger-click="triggerClick">
        <template v-slot="{ visible }">
            <slot :visible="visible" :value="localValue">
                <vx-action-icon
                    icon="CalendarIcon"
                    :hover-icon="visible ? 'XIcon' : 'CalendarIcon'"
                    :hover-color="visible ? 'danger' : 'primary'"
                    active-color="primary"
                    :active="value && !!value.start"
                    @click="tryClear($event, visible)"
                />
            </slot>
            <vs-dropdown-menu class="dropdown-menu">
                <slot name="top"></slot>
                <div class="flex justify-between">
                    <slot name="left"> </slot>

                    <v-date-picker
                        ref="calendar"
                        v-model="localValue"
                        :attributes="attributes_x"
                        :disabled-dates="disabledDates"
                        :available-dates="availableDates"
                        :is-dark="isThemeDark"
                        is-range
                    />
                    <slot name="right">
                        <div v-if="showPresets_x" class="flex flex-col ml-2 preset-range-section">
                            <vs-button
                                v-for="(button, index) in rangeButtons"
                                class="preset-button whitespace-nowrap text-left pl-2 ml-1"
                                :class="{ active: checkRangeSelected(button.range) }"
                                :key="index"
                                @click="localValue = button.range"
                            >
                                {{ button.title }}
                            </vs-button>
                        </div>
                    </slot>
                </div>
                <slot name="bottom">
                    <div class="w-full flex items-center justify-center mt-3 pt-2 bottom-section">
                        <template v-if="hasRange">
                            <h5>{{ startDateString }}</h5>
                            <vx-basic-icon icon="ArrowRightIcon" size="7" class="mx-3" />
                            <h5>{{ endDateString }}</h5>
                        </template>
                        <h5 v-else>No Range Selected</h5>
                    </div>
                </slot>
            </vs-dropdown-menu>
        </template>
    </vx-dropdown>
</template>

<script>
import { mapGetters } from 'vuex'
import VDatePicker from 'v-calendar/lib/components/date-picker.umd'

export default {
    name: 'vx-date-range-dropdown',
    components: {
        VDatePicker
    },
    props: {
        value: {
            type: Object
        },
        attributes: {
            type: Array,
            default: () => []
        },
        disabledDates: {
            type: [Object, Array]
        },
        availableDates: {
            type: [Object, Array]
        },
        closeOnSelect: {
            type: Boolean
        },
        triggerClick: {
            type: Boolean
        },
        showPresets: {
            type: Boolean
        },
        customPresets: {
            type: Array
        }
    },
    watch: {
        startDate(newV) {
            if (!newV || !this.$refs.calendar) {
                return
            }
            this.$refs.calendar.move(newV)
        }
    },
    data() {
        return {
            now: new Date().getStartOfDay()
        }
    },
    computed: {
        ...mapGetters({
            isThemeDark: 'user/isThemeDark'
        }),
        hasRange() {
            return this.startDate || this.endDate
        },
        showPresets_x() {
            return this.showPresets || (this.customPresets && this.customPresets.any())
        },
        localValue: {
            get() {
                // Either use value or start/end NEVER both
                // checking value so that we don't produce { start: null, end: null }
                if (!this.value) {
                    return null
                }

                return {
                    start: this.startDate,
                    end: this.endDate
                }
            },
            set(val) {
                if (val && val.end) {
                    val.end = val.end.getEndOfDay()
                }
                console.log(val)
                this.$emit('input', val)

                if (!this.closeOnSelect) {
                    return
                }
                setTimeout(
                    function () {
                        this.$refs.dropdown.triggerClose()
                    }.bind(this),
                    50
                )
            }
        },
        startDate() {
            if (!this.value || !this.value.start) {
                return null
            }
            return new Date(this.value.start).getStartOfDay()
        },
        endDate() {
            if (!this.value || !this.value.end) {
                return null
            }
            return new Date(this.value.end).getEndOfDay()
        },
        attributes_x() {
            return [
                ...this.attributes,
                {
                    key: 'today',
                    bar: 'red',
                    dates: this.now
                }
            ]
        },
        rangeButtons() {
            if (this.customPresets) {
                return this.customPresets.map(({ title, range }) => {
                    return {
                        title,
                        range: {
                            start: range.start && new Date(range.start).getStartOfDay(),
                            end: range.end && new Date(range.end).getEndOfDay()
                        }
                    }
                })
            }
            return [
                {
                    title: 'Today',
                    range: this.today
                },
                {
                    title: 'Yesterday',
                    range: this.yesterday
                },
                {
                    title: 'This Week',
                    range: this.thisWeek
                },
                {
                    title: 'This Month',
                    range: this.thisMonth
                },
                {
                    title: 'Last Month',
                    range: this.lastMonth
                },
                {
                    title: this.getFiscalYearTitle(this.thisFinancialYear),
                    range: this.thisFinancialYear
                },
                {
                    title: this.getFiscalYearTitle(this.lastFinancialYear),
                    range: this.lastFinancialYear
                }
            ]
        },
        startDateString() {
            if (!this.startDate) {
                return 'Dawn of Time'
            }
            return this.formatUTCDateToDate(this.startDate)
        },
        endDateString() {
            if (!this.endDate) {
                return 'End of Time'
            }
            return this.formatUTCDateToDate(this.endDate)
        },
        today() {
            return this.now.getDayRange()
        },
        yesterday() {
            return this.now.addDays(-1).getDayRange()
        },
        thisWeek() {
            return this.now.getWorkWeekRange()
        },
        thisMonth() {
            return this.now.getMonthRange()
        },
        lastMonth() {
            return this.now.addMonths(-1).getMonthRange()
        },
        thisFinancialYear() {
            return this.now.getFiscalYearRange()
        },
        lastFinancialYear() {
            return this.now.addYears(-1).getFiscalYearRange()
        }
    },
    methods: {
        formatUTCDateToDate(value) {
            if (!value) {
                return ''
            }
            return new Date(value).toLocaleDateString('en-AU').slice(0, 10)
        },
        getFiscalYearTitle(range) {
            var startYear = range.start.getFullYear()
            var endYear = range.end.getFullYear().toString().substr(2)
            return `FY ${startYear}-${endYear}`
        },
        checkRangeSelected(range) {
            if (!range || !this.localValue) {
                return range === this.localValue
            }
            var startEqual =
                (range.start && range.start.getTime()) ==
                (this.localValue.start && this.localValue.start.getTime())
            var endEqual =
                (range.end && range.end.getTime()) ==
                (this.localValue.end && this.localValue.end.getTime())
            return startEqual && endEqual
        },
        tryClear(e, visible) {
            if (!visible) {
                return
            }
            e.preventDefault()
            e.stopPropagation()
            this.localValue = null
        }
    }
}
</script>

<style lang="scss" scoped>
.dropdown-menu {
    ::v-deep .vc-container {
        background-color: transparent;
        border: none !important;
    }
}

.preset-button {
    background: transparent !important;
    color: rgba(var(--vs-dark), 1 !important);
    &:hover {
        background: rgba(var(--vs-primary), 0.3) !important;
    }
    &.active {
        background: rgba(var(--vs-primary), 0.8) !important;
        color: rgba(var(--vs-light), 1) !important;
    }
}

.bottom-section {
    border-top: 1px solid rgba(var(--vs-dark), 1);
}

.preset-range-section {
    border-left: 1px solid rgba(var(--vs-dark), 1);
    padding-left: 0.5rem;
}
</style>
